/* =====================================================================
   ZARDO MEGA MENU — <zardo-mega-menu>
   Drives the header mega menu rendered from nested theme blocks
   (blocks/_zardo-mega-*.liquid). Styles: assets/zardo-mega-menu.css.

   DOM contract (all inside the header section):
     [data-zardo-mega-bar]             header bar <ul>; links carry [data-zardo-mega-link="key"]
     [data-zardo-mega-trigger="key"]   header bar item that opens a panel (mounted from the block's template)
     [data-zardo-mega-open]            hamburger: opens the mobile drawer
     <zardo-mega-menu data-trigger="hover|click">
       [data-zardo-mega-backdrop]      desktop dimmer
       [data-zardo-mega-panel="key"]   one panel per mega menu block
         template[data-zardo-mega-nav-template]  bar trigger + drawer row, mounted by mountNavigation()
         [data-zardo-mega-tablist]     sidebar list, filled from each section's template[data-zardo-mega-tab-template]
         [data-zardo-mega-tab="id"]    sidebar entry (desktop tab / mobile tile)
         [data-zardo-mega-section="id"] content pane (desktop) / accordion or level (mobile)
           [data-zardo-mega-toggle]    accordion toggle (mobile)
       [data-zardo-mega-drawer]        mobile drawer root
         [data-zardo-mega-drawer-list] menu list; links carry [data-zardo-mega-link="key"]
         [data-zardo-mega-goto="key"]  opens a panel as a level (mounted from the block's template)
       [data-zardo-mega-back] / [data-zardo-mega-close]

   Desktop (>= 768px): hover intent or click opens a panel; the sidebar
   switches panes on hover, click or arrow keys; Escape, outside click and
   focus leaving the panel close it.
   Mobile (< 768px): the drawer slides in under the header (kept visible,
   its hamburger becoming the X); panels and (in tiles layout) sections are
   pushed as levels with back navigation.
   ===================================================================== */
(() => {
  'use strict';

  if (window.customElements.get('zardo-mega-menu')) return;

  const DESKTOP_QUERY = '(min-width: 768px)';
  const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
  const MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const OPEN_DELAY = 40;
  const CLOSE_DELAY = 180;
  const TAB_DELAY = 40;
  const ANIMATION_MS = 300;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const normalizeKey = (value) => String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const isVisible = (element) => element.getClientRects().length > 0;

  class ZardoMegaMenu extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.sectionElement = this.closest('.shopify-section') || document.body;
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.hoverQuery = window.matchMedia(HOVER_QUERY);
      this.motionQuery = window.matchMedia(MOTION_QUERY);
      this.hoverMode = this.dataset.trigger !== 'click';

      this.backdrop = this.querySelector('[data-zardo-mega-backdrop]');
      this.drawer = this.querySelector('[data-zardo-mega-drawer]');
      this.panels = new Map();
      this.triggers = new Map();
      this.openKey = null;
      this.openedByEditor = false;
      this.drawerOpen = false;
      this.opener = null;
      this.levels = [];
      this.timers = { open: 0, close: 0, tab: 0 };

      this.querySelectorAll('[data-zardo-mega-panel]').forEach((panel) => {
        this.panels.set(panel.dataset.zardoMegaPanel, panel);
      });

      this.mountNavigation();

      this.sectionElement.querySelectorAll('[data-zardo-mega-trigger]').forEach((trigger) => {
        const key = trigger.dataset.zardoMegaTrigger;

        if (this.panels.has(key)) {
          this.triggers.set(key, trigger);
        } else {
          ['aria-haspopup', 'aria-expanded', 'aria-controls'].forEach((name) => trigger.removeAttribute(name));
        }
      });

      this.applyMode();
      this.desktopQuery.addEventListener('change', () => {
        this.closeEverything({ instant: true });
        this.applyMode();
      }, { signal });

      this.bindTriggers(signal);
      this.bindPanels(signal);
      this.bindDrawer(signal);
      this.bindDocument(signal);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.clearTimers();
      document.documentElement.classList.remove('zardo-menu-open');
      this.sectionElement?.classList.remove('zmm-is-open');
    }

    get isDesktop() {
      return this.desktopQuery.matches;
    }

    get usesHover() {
      return this.hoverMode && this.hoverQuery.matches;
    }

    get instant() {
      return this.motionQuery.matches;
    }

    /* ------------------------------------------------------------------ setup */

    // Theme blocks can't be read by the header's Liquid, so each block renders its navigation
    // entries in <template> elements. Move them into place: the bar trigger and drawer row replace
    // the menu link with the same key (or are appended), and each section's tab joins the sidebar.
    mountNavigation() {
      if (this.dataset.mounted === 'true') return;
      this.dataset.mounted = 'true';

      const targets = {
        bar: this.sectionElement.querySelector('[data-zardo-mega-bar]'),
        drawer: this.querySelector('[data-zardo-mega-drawer-list]'),
      };

      this.panels.forEach((panel, key) => {
        const template = panel.querySelector('template[data-zardo-mega-nav-template]');

        if (template) {
          Object.entries(targets).forEach(([context, list]) => {
            const source = template.content.querySelector(`[data-zardo-mega-nav-item="${context}"]`);
            if (!list || !source) return;

            const item = source.cloneNode(true);
            const control = item.querySelector('[data-zardo-mega-trigger], [data-zardo-mega-goto]');
            const link = Array.from(list.querySelectorAll('[data-zardo-mega-link]')).find((element) => element.dataset.zardoMegaLink === key);
            const href = link?.getAttribute('href');

            if (control?.tagName === 'A' && href && (!control.getAttribute('href') || control.getAttribute('href') === '#')) {
              control.setAttribute('href', href);
            }

            const existing = link?.closest('li');
            if (existing) existing.replaceWith(item);
            else list.append(item);
          });

          template.remove();
        }

        const tablist = panel.querySelector('[data-zardo-mega-tablist]');
        panel.querySelectorAll('template[data-zardo-mega-tab-template]').forEach((tabTemplate) => {
          if (tablist) tablist.append(tabTemplate.content.cloneNode(true));
          tabTemplate.remove();
        });
      });
    }

    applyMode() {
      const desktop = this.isDesktop;

      this.panels.forEach((panel) => {
        const list = panel.querySelector('[data-zardo-mega-tablist]');
        const tabs = panel.querySelectorAll('[data-zardo-mega-tab]');
        const sections = panel.querySelectorAll('[data-zardo-mega-section]');

        if (list) {
          if (desktop) {
            list.setAttribute('role', 'tablist');
            list.setAttribute('aria-orientation', 'vertical');
          } else {
            list.removeAttribute('role');
            list.removeAttribute('aria-orientation');
          }
        }

        tabs.forEach((tab) => {
          if (desktop) {
            tab.setAttribute('role', 'tab');
          } else {
            tab.removeAttribute('role');
            tab.removeAttribute('aria-selected');
            tab.removeAttribute('tabindex');
          }
        });

        sections.forEach((section) => {
          if (desktop) {
            section.setAttribute('role', 'tabpanel');
            section.setAttribute('aria-labelledby', `zmm-tab-${section.dataset.zardoMegaSection}`);
            section.setAttribute('tabindex', '-1');
          } else {
            ['role', 'aria-labelledby', 'tabindex'].forEach((name) => section.removeAttribute(name));
          }
        });

        if (desktop) {
          this.activateSection(panel, panel.dataset.active || sections[0]?.dataset.zardoMegaSection);
        }
      });
    }

    bindTriggers(signal) {
      this.triggers.forEach((trigger, key) => {
        trigger.addEventListener('pointerenter', (event) => {
          if (!this.isDesktop || !this.usesHover || event.pointerType === 'touch') return;
          this.scheduleOpen(key);
        }, { signal });

        trigger.addEventListener('pointerleave', () => {
          if (!this.isDesktop || !this.usesHover) return;
          this.scheduleClose();
        }, { signal });

        trigger.addEventListener('click', (event) => {
          if (!this.isDesktop) return;

          const fromKeyboard = event.detail === 0;
          const href = trigger.getAttribute('href');
          const isLink = trigger.tagName === 'A' && href && href !== '#';

          // Mouse click in hover mode follows the link (the panel is already open on hover).
          if (this.usesHover && !fromKeyboard && isLink) return;

          event.preventDefault();

          if (this.openKey === key) {
            this.closePanel({ restoreFocus: fromKeyboard });
          } else {
            this.openPanel(key, { focus: fromKeyboard });
          }
        }, { signal });

        trigger.addEventListener('keydown', (event) => {
          if (!this.isDesktop) return;

          if (event.key === 'ArrowDown' || event.key === ' ') {
            event.preventDefault();
            this.openPanel(key, { focus: true });
          } else if (event.key === 'Escape' && this.openKey === key) {
            event.preventDefault();
            this.closePanel({ restoreFocus: true });
          }
        }, { signal });
      });
    }

    bindPanels(signal) {
      this.panels.forEach((panel, key) => {
        panel.addEventListener('pointerenter', () => {
          if (this.isDesktop && this.usesHover) this.clearTimer('close');
        }, { signal });

        panel.addEventListener('pointerleave', (event) => {
          if (this.isDesktop && this.usesHover && event.pointerType !== 'touch') this.scheduleClose();
        }, { signal });

        panel.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          event.stopPropagation();

          if (this.isDesktop) {
            this.closePanel({ restoreFocus: true });
          } else {
            this.popLevel();
          }
        }, { signal });

        panel.addEventListener('focusout', (event) => {
          if (!this.isDesktop || this.openKey !== key) return;
          const next = event.relatedTarget;
          if (!next) return;
          if (panel.contains(next) || this.triggers.get(key)?.contains(next)) return;
          this.closePanel();
        }, { signal });

        panel.querySelectorAll('[data-zardo-mega-tab]').forEach((tab) => {
          const id = tab.dataset.zardoMegaTab;

          tab.addEventListener('pointerenter', (event) => {
            if (!this.isDesktop || !this.hoverQuery.matches || event.pointerType === 'touch') return;
            this.clearTimer('tab');
            this.timers.tab = window.setTimeout(() => this.activateSection(panel, id), TAB_DELAY);
          }, { signal });

          tab.addEventListener('pointerleave', () => this.clearTimer('tab'), { signal });

          tab.addEventListener('click', () => {
            if (this.isDesktop) {
              this.activateSection(panel, id, { focus: true });
              return;
            }

            const section = panel.querySelector(`[data-zardo-mega-section="${id}"]`);
            if (section && panel.classList.contains('zmm-panel--mobile-tiles')) this.pushLevel(section, tab);
          }, { signal });

          tab.addEventListener('keydown', (event) => {
            if (!this.isDesktop) return;

            const tabs = Array.from(panel.querySelectorAll('[data-zardo-mega-tab]'));
            const index = tabs.indexOf(tab);
            let next = null;

            switch (event.key) {
              case 'ArrowDown': next = tabs[(index + 1) % tabs.length]; break;
              case 'ArrowUp': next = tabs[(index - 1 + tabs.length) % tabs.length]; break;
              case 'Home': next = tabs[0]; break;
              case 'End': next = tabs[tabs.length - 1]; break;
              case 'ArrowRight': {
                const target = panel.querySelector(`[data-zardo-mega-section="${id}"]`)?.querySelector(FOCUSABLE);
                if (target) {
                  event.preventDefault();
                  target.focus();
                }
                return;
              }
              default: return;
            }

            event.preventDefault();
            this.activateSection(panel, next.dataset.zardoMegaTab, { focus: true });
          }, { signal });
        });

        panel.querySelectorAll('[data-zardo-mega-toggle]').forEach((toggle) => {
          toggle.addEventListener('click', () => {
            const section = toggle.closest('[data-zardo-mega-section]');
            if (!section) return;
            const expanded = !section.classList.contains('is-expanded');

            // Opening this one closes the others, which shortens everything above it and
            // would slide the header the visitor just tapped up the screen. Measure the
            // header before and after and give the scroller that height back, so the
            // tapped row stays exactly where their thumb left it.
            const scroller = toggle.closest('.zmm-panel__inner');
            const before = scroller ? toggle.getBoundingClientRect().top : 0;
            this.expandSection(section, expanded);
            if (scroller && expanded) {
              const shift = toggle.getBoundingClientRect().top - before;
              if (shift) scroller.scrollTop += shift;
            }
          }, { signal });
        });

        if (panel.hasAttribute('data-open-first')) {
          const first = panel.querySelector('[data-zardo-mega-section]');
          if (first) this.expandSection(first, true);
        }
      });
    }

    bindDrawer(signal) {
      this.sectionElement.querySelectorAll('[data-zardo-mega-open]').forEach((opener) => {
        opener.addEventListener('click', (event) => {
          event.preventDefault();

          if (this.drawerOpen) {
            this.closeDrawer({ restoreFocus: true });
          } else {
            this.openDrawer(opener);
          }
        }, { signal });
      });

      this.addEventListener('click', (event) => {
        const close = event.target.closest('[data-zardo-mega-close]');
        if (close) {
          event.preventDefault();
          this.closeEverything({ restoreFocus: true });
          return;
        }

        const back = event.target.closest('[data-zardo-mega-back]');
        if (back) {
          event.preventDefault();
          this.popLevel();
          return;
        }

        const goto = event.target.closest('[data-zardo-mega-goto]');
        if (goto) {
          event.preventDefault();
          const panel = this.panels.get(goto.dataset.zardoMegaGoto);
          if (panel) this.pushLevel(panel, goto);
        }
      }, { signal });

      // Tab from the header's X cycles into the drawer as well.
      this.sectionElement.addEventListener('keydown', (event) => {
        if (!this.drawerOpen || this.isDesktop) return;

        if (event.key === 'Tab') {
          this.trapFocus(event);
        } else if (event.key === 'Escape' && !event.defaultPrevented) {
          event.preventDefault();
          this.closeDrawer({ restoreFocus: true });
        }
      }, { signal });
    }

    bindDocument(signal) {
      document.addEventListener('pointerdown', (event) => {
        if (!this.isDesktop || !this.openKey) return;

        const panel = this.panels.get(this.openKey);
        const trigger = this.triggers.get(this.openKey);
        if (panel.contains(event.target) || trigger?.contains(event.target)) return;

        this.closePanel();
      }, { signal, capture: true });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isDesktop && this.openKey) this.closePanel({ restoreFocus: true });
      }, { signal });

      // Brand shortcuts elsewhere on the page (homepage logos, carousel tabs) open the matching menu.
      document.addEventListener('click', (event) => {
        const shortcut = event.target.closest('[data-zardo-brand-menu], [data-zardo-open-menu-section]');

        if (shortcut) {
          const key = this.resolveKey(shortcut.getAttribute('data-zardo-brand-menu') || shortcut.getAttribute('data-zardo-open-menu-section'));
          if (!key) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          this.openFromShortcut(key);
          return;
        }

        if (event.target.closest('[data-zardo-cart-drawer-trigger]')) this.closeEverything({ instant: true });
      }, { signal, capture: true });

      // Only reset a page restored from the back/forward cache: pageshow also fires at the end of
      // every normal load, which would shut a menu the visitor opened while the page was loading.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.closeEverything({ instant: true });
      }, { signal });
      window.addEventListener('resize', () => { if (this.drawerOpen) this.measureDrawerTop(); }, { signal });

      if (window.Shopify?.designMode) {
        this.sectionElement.addEventListener('shopify:block:select', (event) => this.handleEditorSelect(event), { signal });
        this.sectionElement.addEventListener('shopify:block:deselect', (event) => this.handleEditorDeselect(event), { signal });
        this.sectionElement.addEventListener('shopify:section:deselect', () => this.closeEverything({ instant: true }), { signal });
      }
    }

    /* ------------------------------------------------------------------ desktop panel */

    scheduleOpen(key) {
      this.clearTimer('close');
      this.clearTimer('open');
      this.timers.open = window.setTimeout(() => this.openPanel(key), OPEN_DELAY);
    }

    scheduleClose() {
      this.clearTimer('open');
      this.clearTimer('close');
      this.timers.close = window.setTimeout(() => this.closePanel(), CLOSE_DELAY);
    }

    openPanel(key, { focus = false, editor = false } = {}) {
      const panel = this.panels.get(key);
      if (!panel) return;

      this.clearTimer('open');
      this.clearTimer('close');

      if (this.openKey && this.openKey !== key) this.closePanel({ instant: true });
      if (this.openKey === key) return;

      this.openKey = key;
      this.openedByEditor = editor;
      panel.hidden = false;
      this.triggers.get(key)?.setAttribute('aria-expanded', 'true');
      this.sectionElement.classList.add('zmm-is-open');
      this.activateSection(panel, panel.dataset.active || panel.querySelector('[data-zardo-mega-section]')?.dataset.zardoMegaSection);

      if (this.backdrop) this.backdrop.hidden = false;

      window.requestAnimationFrame(() => {
        panel.classList.add('is-open');
        this.backdrop?.classList.add('is-visible');
      });

      if (focus) panel.querySelector('[data-zardo-mega-tab].is-active')?.focus();
    }

    closePanel({ instant = false, restoreFocus = false } = {}) {
      const key = this.openKey;
      if (!key) return;

      const panel = this.panels.get(key);
      const trigger = this.triggers.get(key);

      this.clearTimer('open');
      this.clearTimer('close');
      this.openKey = null;
      this.openedByEditor = false;
      trigger?.setAttribute('aria-expanded', 'false');
      this.sectionElement.classList.remove('zmm-is-open');
      panel.classList.remove('is-open');
      this.backdrop?.classList.remove('is-visible');

      const finish = () => {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
        if (!this.openKey && this.backdrop) this.backdrop.hidden = true;
      };

      if (instant || this.instant) finish();
      else window.setTimeout(finish, ANIMATION_MS);

      if (restoreFocus) trigger?.focus({ preventScroll: true });
    }

    activateSection(panel, id, { focus = false } = {}) {
      if (!id) return;

      panel.dataset.active = id;

      panel.querySelectorAll('[data-zardo-mega-tab]').forEach((tab) => {
        const active = tab.dataset.zardoMegaTab === id;
        tab.classList.toggle('is-active', active);

        if (this.isDesktop) {
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.setAttribute('tabindex', active ? '0' : '-1');
        }

        if (active && focus) tab.focus();
      });

      panel.querySelectorAll('[data-zardo-mega-section]').forEach((section) => {
        section.classList.toggle('is-active', section.dataset.zardoMegaSection === id);
      });

      const content = panel.querySelector('[data-zardo-mega-content]');
      if (content) content.scrollTop = 0;
    }

    // Accordion sections (mobile) open one at a time: with several open at once the
    // section headers get pushed far down the drawer and the visitor has to scroll
    // past a whole open section to reach the next one.
    expandSection(section, expanded) {
      if (expanded) {
        const panel = section.closest('[data-zardo-mega-panel]');
        panel?.querySelectorAll('[data-zardo-mega-section].is-expanded').forEach((other) => {
          if (other !== section) this.expandSection(other, false);
        });
      }

      section.classList.toggle('is-expanded', expanded);
      section.querySelector('[data-zardo-mega-toggle]')?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    /* ------------------------------------------------------------------ mobile drawer */

    // The drawer starts where the header section ends, so the announcement bar and the
    // header (with its X) stay visible above it, at the top of the page or once scrolled.
    measureDrawerTop() {
      const top = Math.max(0, Math.round(this.sectionElement.getBoundingClientRect().bottom));
      this.style.setProperty('--zmm-drawer-top', `${top}px`);
    }

    setOpenerState(open) {
      this.sectionElement.querySelectorAll('[data-zardo-mega-open]').forEach((element) => {
        element.setAttribute('aria-expanded', open ? 'true' : 'false');
        const label = open ? element.dataset.labelClose : element.dataset.labelOpen;
        if (label) element.setAttribute('aria-label', label);
      });
    }

    openDrawer(opener = null) {
      if (!this.drawer || this.drawerOpen) return;

      this.drawerOpen = true;
      this.opener = opener || this.sectionElement.querySelector('[data-zardo-mega-open]');
      this.measureDrawerTop();
      this.drawer.hidden = false;
      document.documentElement.classList.add('zardo-menu-open');
      this.setOpenerState(true);

      window.requestAnimationFrame(() => this.drawer.classList.add('is-open'));
      this.drawer.focus({ preventScroll: true });
    }

    closeDrawer({ instant = false, restoreFocus = false } = {}) {
      if (!this.drawer || !this.drawerOpen) return;

      this.drawerOpen = false;
      while (this.levels.length) this.popLevel({ instant: true, focus: false });

      this.drawer.classList.remove('is-open');
      document.documentElement.classList.remove('zardo-menu-open');
      this.setOpenerState(false);

      const finish = () => {
        if (!this.drawerOpen) this.drawer.hidden = true;
      };

      if (instant || this.instant) finish();
      else window.setTimeout(finish, ANIMATION_MS);

      if (restoreFocus) this.opener?.focus({ preventScroll: true });
      this.opener = null;
    }

    pushLevel(level, opener = null) {
      if (this.levels.includes(level)) return;
      if (!this.drawerOpen) this.openDrawer();

      const isPanel = level.hasAttribute('data-zardo-mega-panel');
      if (isPanel) level.hidden = false;

      this.levels.push(level);
      opener?.setAttribute('aria-expanded', 'true');

      // On mobile the panel scrolls in .zmm-panel__inner (the section body in tiles layout), not in the
      // content pane: reset them all, or a scroll left from a previous visit hides "Shop all" under the bar.
      level.querySelectorAll('[data-zardo-mega-content], .zmm-panel__inner, .zmm-section__body').forEach((scroller) => { scroller.scrollTop = 0; });

      window.requestAnimationFrame(() => level.classList.add('is-level-open'));
      window.setTimeout(() => level.querySelector('[data-zardo-mega-back]')?.focus({ preventScroll: true }), this.instant ? 0 : 60);
    }

    popLevel({ instant = false, focus = true } = {}) {
      const level = this.levels.pop();

      if (!level) {
        this.closeDrawer({ restoreFocus: true });
        return;
      }

      level.classList.remove('is-level-open');
      this.querySelectorAll(`[aria-controls="${level.id}"]`).forEach((element) => element.setAttribute('aria-expanded', 'false'));

      const finish = () => {
        if (!level.classList.contains('is-level-open') && level.hasAttribute('data-zardo-mega-panel')) level.hidden = true;
      };

      if (instant || this.instant) finish();
      else window.setTimeout(finish, ANIMATION_MS);

      if (!focus) return;

      const parent = this.levels[this.levels.length - 1] || this.drawer;
      const target = parent?.querySelector(`[aria-controls="${level.id}"]`) || parent?.querySelector('[data-zardo-mega-back], [data-zardo-mega-close]');
      target?.focus({ preventScroll: true });
    }

    trapFocus(event) {
      const layer = this.levels[this.levels.length - 1] || this.drawer;
      if (!layer) return;

      // The header's X (the opener) belongs to the dialog's tab cycle.
      const focusable = Array.from(layer.querySelectorAll(FOCUSABLE)).filter(isVisible);
      if (this.opener && isVisible(this.opener)) focusable.unshift(this.opener);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      const inside = layer.contains(active) || active === this.opener;

      if (event.shiftKey && (active === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ------------------------------------------------------------------ shared */

    closeEverything({ instant = false, restoreFocus = false } = {}) {
      this.closePanel({ instant, restoreFocus });
      this.closeDrawer({ instant, restoreFocus });
    }

    resolveKey(value) {
      const wanted = normalizeKey(value);
      if (!wanted) return null;
      if (this.panels.has(wanted)) return wanted;

      const head = wanted.split('-')[0];

      for (const key of this.panels.keys()) {
        if (key.includes(wanted) || wanted.includes(key)) return key;
        if (head.length > 2 && key.includes(head)) return key;
      }

      return null;
    }

    openFromShortcut(key) {
      if (this.isDesktop) {
        const bounds = this.sectionElement.getBoundingClientRect();
        if (bounds.bottom <= 0 || bounds.top >= window.innerHeight) this.sectionElement.scrollIntoView({ block: 'start' });
        this.openPanel(key, { focus: true });
        return;
      }

      this.openDrawer();
      this.pushLevel(this.panels.get(key));
    }

    handleEditorSelect(event) {
      const panel = event.target.closest('[data-zardo-mega-panel]');
      if (!panel) return;

      const key = panel.dataset.zardoMegaPanel;
      const section = event.target.closest('[data-zardo-mega-section]');

      if (this.isDesktop) {
        this.openPanel(key, { editor: true });
        if (section) this.activateSection(panel, section.dataset.zardoMegaSection);
        return;
      }

      if (!this.drawerOpen) this.openDrawer();

      if (!this.levels.includes(panel)) {
        while (this.levels.length) this.popLevel({ instant: true, focus: false });
        this.pushLevel(panel);
      }

      if (!section) return;

      if (panel.classList.contains('zmm-panel--mobile-tiles')) {
        if (!this.levels.includes(section)) this.pushLevel(section);
      } else {
        this.expandSection(section, true);
      }
    }

    handleEditorDeselect(event) {
      const panel = event.target.closest('[data-zardo-mega-panel]');
      if (!panel) return;
      if (this.isDesktop && this.openedByEditor && this.openKey === panel.dataset.zardoMegaPanel) this.closePanel({ instant: true });
    }

    clearTimer(name) {
      window.clearTimeout(this.timers[name]);
      this.timers[name] = 0;
    }

    clearTimers() {
      Object.keys(this.timers).forEach((name) => this.clearTimer(name));
    }
  }

  window.customElements.define('zardo-mega-menu', ZardoMegaMenu);
})();
